import { RESERVED_SHOP_SLUGS } from '~/utils/reservedShopSlugs'

/**
 * Resolve shop slug from custom domain, route params, or first path segment (path-based tenancy).
 * Domain slug wins: on custom hosts paths are bare (`/admin`, `/cont/login`) so `params.shop`
 * is empty and the first segment is a page name, not the shop.
 */
export function resolveShopSlugFromRoute(
  path: string,
  params: Record<string, string | string[] | undefined | null>,
  domainSlug?: string | null,
): string {
  if (domainSlug) {
    return domainSlug.trim().toLowerCase()
  }

  const raw = params.shop
  const fromParam = Array.isArray(raw) ? raw[0] : raw
  if (typeof fromParam === 'string' && fromParam.trim()) {
    const normalized = fromParam.trim().toLowerCase()
    if (!RESERVED_SHOP_SLUGS.has(normalized)) {
      return normalized
    }
  }

  const first = path.split('/').filter(Boolean)[0]?.toLowerCase() || ''
  if (first && !RESERVED_SHOP_SLUGS.has(first)) {
    return first
  }

  return ''
}

/**
 * Client/store helper: resolve the current shop slug using live route + shop-domain state.
 */
export function resolveCurrentClientShopSlug(): string {
  const nuxtApp = tryUseNuxtApp()
  const domainState = nuxtApp
    ? useState<{ shop: { slug?: string } | null }>('shop-domain')
    : null
  const domainSlug = domainState?.value?.shop?.slug || ''

  const route = nuxtApp?.$router.currentRoute.value
  if (route) {
    return resolveShopSlugFromRoute(
      route.path,
      route.params as Record<string, string | string[]>,
      domainSlug,
    )
  }

  if (import.meta.client && typeof window !== 'undefined') {
    return resolveShopSlugFromRoute(window.location.pathname, {}, domainSlug)
  }

  return domainSlug.trim().toLowerCase()
}
